import type { TenantSummary } from "@storytell/types";
import { StButton } from "@storytell/ui/buttons";
import { StDialog, StDialogCloseButton, StDialogTitle } from "@storytell/ui/dialog";
import { TbCheck, TbSearch, TbX } from "solid-icons/tb";
import { type Component, createSignal, For, onCleanup, onMount, Show } from "solid-js";
import { twMerge } from "tailwind-merge";
import { NavRowLabel } from "~/components/navigation/NavRowLabel";
import { useOrgScope } from "~/hooks/useOrgScope";
import { useI18n } from "~/i18n";
import { trackFeature } from "~/lib/trackFeature";

const LISTBOX_ID = "organization-switcher-results";
const optionId = (index: number) => `organization-switcher-option-${index}`;

const OrganizationListView: Component<{ onClose: () => void }> = (props) => {
  const { t } = useI18n();
  const scope = useOrgScope();
  const [query, setQuery] = createSignal("");
  const [activeIndex, setActiveIndex] = createSignal(0);
  let listRef: HTMLDivElement | undefined;
  let inputRef: HTMLInputElement | undefined;

  // Names collide — every organization still on the signup default shares one —
  // so the row prints the owner's address to tell them apart, and typing what
  // the row shows has to find it.
  const results = () => {
    const needle = query().trim().toLowerCase();
    return scope.organizations().filter((tenant) => {
      const fields = [scope.organizationName(tenant), tenant.organizationOwnerEmail];
      return fields.some((field) => field?.toLowerCase().includes(needle));
    });
  };

  const isCurrent = (tenant: TenantSummary) => tenant.organizationId === scope.currentOrganization()?.organizationId;

  const select = (tenant: TenantSummary) => {
    if (!isCurrent(tenant)) trackFeature({ feature: "org", action: "switch", via: "menu" });
    scope.switchToOrganization(tenant.organizationId);
    props.onClose();
  };

  const moveActive = (delta: number) => {
    const count = results().length;
    if (count === 0) return;
    setActiveIndex((activeIndex() + delta + count) % count);
    listRef?.querySelectorAll<HTMLElement>("[data-organization-option]")[activeIndex()]?.scrollIntoView({
      block: "nearest",
    });
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      moveActive(1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      moveActive(-1);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const tenant = results()[activeIndex()];
      if (tenant) select(tenant);
    }
  };

  // Kobalte parks focus on the dialog container when it opens; take it back on
  // the next frame so the first keystroke lands in the search box.
  onMount(() => {
    const frame = requestAnimationFrame(() => inputRef?.focus());
    onCleanup(() => cancelAnimationFrame(frame));
  });

  return (
    <div class="flex min-h-0 flex-col">
      <div class="mb-3 flex min-h-7 items-center pr-16">
        <StDialogTitle class="text-lg font-semibold text-primary-text">
          {t("organization.switcher.heading")}
        </StDialogTitle>
      </div>

      <div class="relative mb-2">
        <TbSearch class="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-text" />
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          autocomplete="off"
          aria-expanded="true"
          aria-controls={LISTBOX_ID}
          aria-activedescendant={optionId(activeIndex())}
          aria-label={t("organization.switcher.search_placeholder")}
          placeholder={t("organization.switcher.search_placeholder")}
          value={query()}
          onInput={(e) => {
            setQuery(e.currentTarget.value);
            setActiveIndex(0);
          }}
          onKeyDown={handleKeyDown}
          class="w-full rounded-lg border border-primary-border bg-canvas py-2 pr-3 pl-10 text-sm text-primary-text outline-none placeholder:text-muted-text focus-visible:ring-2 focus-visible:ring-brand"
        />
      </div>

      {/* Rows are not tab stops: focus stays on the input and moves via aria-activedescendant. */}
      <div
        ref={listRef}
        id={LISTBOX_ID}
        role="listbox"
        aria-label={t("organization.switcher.heading")}
        class="max-h-[50vh] min-h-0 flex-1 overflow-y-auto"
      >
        <For each={results()}>
          {(tenant, index) => (
            <div
              id={optionId(index())}
              data-organization-option
              role="option"
              tabIndex={-1}
              aria-selected={activeIndex() === index()}
              aria-current={isCurrent(tenant) || undefined}
              onClick={() => select(tenant)}
              onMouseMove={() => setActiveIndex(index())}
              class={twMerge(
                "flex w-full cursor-pointer items-center gap-2.5 rounded-md px-3 py-2 text-left text-sm text-primary-text transition-colors",
                activeIndex() === index() && "bg-inset"
              )}
            >
              <TbCheck class="size-4 shrink-0" classList={{ invisible: !isCurrent(tenant) }} />
              <NavRowLabel
                name={scope.organizationName(tenant)}
                detail={scope.organizationRowDetail(tenant)}
                current={isCurrent(tenant)}
              />
            </div>
          )}
        </For>

        <Show when={results().length === 0}>
          <p class="px-3 py-6 text-center text-sm text-secondary-text">{t("organization.switcher.no_results")}</p>
        </Show>
      </div>
    </div>
  );
};

export const OrganizationSwitcherModal: Component<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
}> = (props) => {
  return (
    <StDialog
      open={props.open}
      onOpenChange={props.onOpenChange}
      overlayProps={{ class: "fixed z-[110] inset-0 bg-black/50 dark:bg-white/10" }}
      wrapperProps={{ class: "fixed z-[110] inset-0 flex items-center justify-center pointer-events-none" }}
      contentProps={{ class: "max-w-md flex flex-col max-h-[80vh]" }}
      content={
        <>
          <StDialogCloseButton as={StButton} class="absolute top-8 right-8 z-10 size-7">
            <TbX class="size-4" />
          </StDialogCloseButton>
          <OrganizationListView onClose={() => props.onOpenChange(false)} />
        </>
      }
    />
  );
};
